import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Menu,
  MenuItem,
  IconButton,
  Avatar,
} from '@mui/material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const Navbar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const navItems = [
    { label: 'Dashboard', path: '/dashboard' },
    { label: 'Accounts', path: '/accounts' },
    { label: 'Transactions', path: '/transactions' },
    { label: 'Budgets', path: '/budgets' },
    { label: 'Analytics', path: '/analytics' },
  ];

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleMenuClose();
    navigate('/login');
  };

  const handleProfile = () => {
    handleMenuClose();
    navigate('/profile');
  };

  return (
    <AppBar
      position="static"
      elevation={0}
      sx={{
        background: '#faf8f3',
        color: '#0a0a0a',
        borderBottom: '1px solid #eaeaea',
        boxShadow: 'none',
      }}
    >
      <Toolbar sx={{ py: 0.5, minHeight: 64 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
          <Box
            component={Link}
            to={user ? '/dashboard' : '/'}
            sx={{
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none',
              color: 'inherit',
            }}
          >
            <Box
              component="img"
              src="/logo.png"
              alt="Spenwell"
              sx={{
                width: 28,
                height: 28,
                borderRadius: 1,
                mr: 1.5,
              }}
            />
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontWeight: 600,
                letterSpacing: '-0.02em',
                fontSize: '1.05rem',
                color: '#0a0a0a',
              }}
            >
              Spenwell
            </Typography>
          </Box>
        </Box>

        {user ? (
          <>
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 0.5, mr: 2 }}>
              {navItems.map((item) => {
                const active = location.pathname === item.path;
                return (
                  <Button
                    key={item.path}
                    component={Link}
                    to={item.path}
                    disableRipple
                    sx={{
                      color: active ? '#0a0a0a' : '#5c5c5c',
                      fontWeight: active ? 600 : 500,
                      fontSize: '0.875rem',
                      px: 1.5,
                      py: 0.75,
                      borderRadius: 1.5,
                      backgroundColor: 'transparent',
                      transition: 'color 0.15s ease, background-color 0.15s ease',
                      '&:hover': {
                        backgroundColor: 'rgba(10, 10, 10, 0.04)',
                        color: '#0a0a0a',
                      },
                    }}
                  >
                    {item.label}
                  </Button>
                );
              })}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography
                variant="body2"
                sx={{
                  display: { xs: 'none', sm: 'block' },
                  fontWeight: 500,
                  color: '#5c5c5c',
                }}
              >
                {user.firstName}
              </Typography>
              <IconButton
                size="small"
                edge="end"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenuOpen}
                sx={{ p: 0.5 }}
              >
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: '#1a1a1a',
                    color: '#faf8f3',
                    fontWeight: 600,
                    fontSize: '0.85rem',
                  }}
                >
                  {user.firstName.charAt(0).toUpperCase()}
                </Avatar>
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                slotProps={{
                  paper: {
                    sx: {
                      mt: 0.5,
                      borderRadius: 2,
                      border: '1px solid #eaeaea',
                      boxShadow: '0 4px 12px rgba(15,15,15,0.06)',
                    },
                  },
                }}
              >
                <MenuItem onClick={handleProfile} sx={{ fontSize: '0.875rem' }}>Profile</MenuItem>
                <MenuItem onClick={handleLogout} sx={{ fontSize: '0.875rem' }}>Logout</MenuItem>
              </Menu>
            </Box>
          </>
        ) : (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              component={Link}
              to="/login"
              sx={{
                fontWeight: 500,
                fontSize: '0.875rem',
                color: '#0a0a0a',
                px: 2,
                '&:hover': { backgroundColor: 'rgba(10,10,10,0.04)' },
              }}
            >
              Login
            </Button>
            <Button
              component={Link}
              to="/register"
              variant="contained"
              sx={{
                fontWeight: 500,
                fontSize: '0.875rem',
                px: 2.5,
                background: '#1a1a1a',
                color: '#ffffff',
                '&:hover': { background: '#000000' },
              }}
            >
              Sign Up
            </Button>
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
